import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import CustomerTypesModal from '@/modules/configurationModule/detail/accounting/billable/customer-types/components/customer-types-modal/customer-types-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    CustomerTypeCreateRequest, CustomerTypePatchInBatchRequest,
    CustomerTypeResponse
} from '@/modules/configurationModule/detail/accounting/billable/customer-types/services/customer-types.dto'
import {
    CustomerTypesGridSidepanelComboElements,
    CustomerTypesGridState, initialCustomerTypesGridState
} from '@/modules/configurationModule/detail/accounting/billable/customer-types/components/customer-types-grid/customer-types.model'
import { StrictNullable } from '@/shared/types/app.types'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import {
    ConfigurationCustomerResponse
} from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import {
    ConfigurationListSidePanelEntity
} from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import {
    configurationLocalAccountingBillableCustomerService
} from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.service'
import ConfigurationListSidepanelComp
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import ConfigurationListSidepanel
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        CustomerTypesModal,
        ConfigurationListSidepanel
    }
})
export default class CustomerTypesGrid extends BaseCrudServerSideComponent<CustomerTypeResponse> implements BaseCrud {
    @Ref() readonly customerTypesModalWrapper!: any
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: CustomerTypesGridState
    constructor() {
        super()
        this.state = initialCustomerTypesGridState()
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(customerType: CustomerTypeResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (customerType) {
            this.showModal(this.$tc('configuration.actions.update'), customerType, this.customerTypesModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.customerTypesModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    onRowClicked(event: RowClickedEvent): void {
        const customerGroupSelected: CustomerTypeResponse = event.data
        this.configurationListSidepanel.show({
            title: customerGroupSelected.name,
            entities: [
                {
                    id: CustomerTypesGridSidepanelComboElements.CLIENTS,
                    entityKey: 'configuration.sections.client'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                switch (entity.id as CustomerTypesGridSidepanelComboElements) {
                case CustomerTypesGridSidepanelComboElements.CLIENTS:
                    return this._getCustomerGroupCustomers(customerGroupSelected)
                }
            }
        })
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, customerType: CustomerTypeResponse[]): Promise<void> {
        if (customerType?.length) {
            const batchRequests = customerType.filter(customerType => customerType.active !== active)
                .map(customerType => ({
                    id: customerType.id,
                    active
                }))
            if (await this._patchCustomerTypes(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: CustomerTypeCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createCustomerType(createRequest)) {
                this.customerTypesModalWrapper.hide()
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: CustomerTypePatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchCustomerTypes([batchRequest])) {
                this.customerTypesModalWrapper?.hide()
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteCustomerTypes(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    // SERVICE CALLS ///

    private async _getCustomerGroupCustomers(customerGroupSelected: CustomerTypeResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'type.id': GridUtils.getNumberFilterModel('equals', customerGroupSelected.id)
        }
        const laboratoryId = this.$store.getters.labId
        const response = await this.$_toCallUnique(configurationLocalAccountingBillableCustomerService.paginator(filterModel, params, laboratoryId))
        return response?.content.map((item: ConfigurationCustomerResponse) => item.name) ?? []
    }

    private async _createCustomerType(createRequest: CustomerTypeCreateRequest): Promise<StrictNullable<CustomerTypeResponse>> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchCustomerTypes(batchRequests: CustomerTypePatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteCustomerTypes(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
            if (response && response.length !== ids.length) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }
}
